<template>
  <d-card class="card-small h-100">
    <d-card-header class="border-bottom">
      <h6 class="m-0">{{ title }}</h6>
      <div class="block-handle"></div>
    </d-card-header>

    <d-card-body class="pt-0">

      <div ref="legend"></div>
      <canvas v-if="isLoading == false"
        height="90"
        ref="canvas"
        class="sales-canvas"
      ></canvas>
      <d-container class="d-flex justify-content-center mt-5 mb-5" fluid v-else>
        <b-spinner label="Spinning"></b-spinner>
      </d-container>

      <d-row class="mr-3 d-flex justify-content-start">
        <d-col v-for="affiliate in affiliates" class="network col-2 d-none d-lg-block" v-if="affiliate.amount !== 0">
          <div class="bg rounded text-center p-2 network__item" :style="{ backgroundColor: affiliate.network.color }">
          </div>
          <span class="graph-text">{{ affiliate.network.human_readable_name }}</span>
        </d-col>
      </d-row>
    </d-card-body>
  </d-card>
</template>

<script>
    import Chart from '@/utils/chart';
    import CurrentDate from '@/helpers/date';
    import notification from '@/mixins/notification';
    import { SummaryService, PerformanceService, AffiliateNetworkService } from '@/main';

    export default {
        mixins: [notification],
        name: 'sales-report',

        props: {
            graphData: null,
            /**
            * The component title.
            */
            title: {
                default: `Total revenue per network for ${CurrentDate.getCurrentMonth()}  ${CurrentDate.getCurrentYear()}`,
            },
            /**
            * The Chart.js options.
            */
            chartOptions: {
                type: Object,
                default() {
                    return Object.create(null);
                },
            },
        },

        data() {
            return {
                isLoading: true,
                chart: '',
                affiliates: [],
                graphAffiliates: null,
                endMonth: CurrentDate.getLastDayCurrentMonth(),

                monthlySalesData: {
                    labels: [],
                    datasets: [],
                },
            };
        },

        methods: {
            updateChart() {
                this.chart.data.labels = this.monthlySalesData.labels
                this.chart.data.datasets = this.monthlySalesData.datasets
                this.affiliates = this.graphAffiliates
                this.chart.update()
            },

            async update(affiliateInfo) {
                this.graphAffiliates = affiliateInfo
                this.monthlySalesData.labels = []
                this.monthlySalesData.datasets = []

                for (let i = 1; i <= this.endMonth; i++) {
                    this.monthlySalesData.labels.push(i)
                }

                for (let i = 0; i < affiliateInfo.length; i++) {
                    this.monthlySalesData.datasets.push({
                        title: affiliateInfo[i].network.human_readable_name,
                        fill: 'start',
                        data: Object.values(affiliateInfo[i].daily_sum),
                        backgroundColor: affiliateInfo[i].network.color + 'B3',
                        pointBackgroundColor: affiliateInfo[i].network.color + 'B3',
                        pointHoverBackgroundColor: affiliateInfo[i].network.color + 'B3',
                        borderWidth: 1.5,
                    })
                }

                this.updateChart()
            },

            async createTransactionsGraph () {
                const affiliateInfo = await SummaryService.getSummaryTransactions(CurrentDate.getFirstDayCurrentMonth(), CurrentDate.getDateToday(), '')
                this.affiliates = affiliateInfo

                for (let i = 1; i <= this.endMonth; i++) {
                    this.monthlySalesData.labels.push(i)
                }

                for (let i = 0; i < affiliateInfo.length; i++) {
                    this.monthlySalesData.datasets.push({
                        title: affiliateInfo[i].network.human_readable_name,
                        fill: 'start',
                        data: Object.values(affiliateInfo[i].daily_sum),
                        backgroundColor: affiliateInfo[i].network.color + 'B3',
                        pointBackgroundColor: affiliateInfo[i].network.color + 'B3',
                        pointHoverBackgroundColor: affiliateInfo[i].network.color + 'B3',
                        borderWidth: 1.5,
                    })
                }

                this.isLoading = false
            }
        },

        async mounted() {
            await this.createTransactionsGraph()

            const chartOptions = {
                ...{
                    onResize: (size) => {
                        this.chart.options.tooltips.enabled = size.height < 140 ? false : true;
                        this.chart.options.scales.xAxes[0].ticks.display = size.height < 140 ? false : true;
                        this.chart.update()
                    },

                    legend: false,

                    tooltips: {
                        enabled: true,
                        mode: 'index',
                        position: 'nearest',

                        callbacks: {
                            title(tooltipItem, data) {
                                // Return value for title
                                return `${tooltipItem[0].xLabel} ${CurrentDate.getCurrentMonth()}`;
                            },

                            label(tooltipItem, data) {
                                if (tooltipItem.yLabel !== 0) {
                                    let label = data.datasets[tooltipItem.datasetIndex].label || `${data.datasets[tooltipItem.datasetIndex].title} €`;

                                    if (label) {
                                        label += ' ';
                                    }

                                    label += tooltipItem.yLabel.toFixed(2);
                                    return label;
                                }
                            },

                            footer(tooltipItem, data) {
                                let total = 0

                                for (let i = 0; i < tooltipItem.length; i++) {
                                    if (tooltipItem[i].yLabel !== 0) {
                                        total += tooltipItem[i].yLabel
                                    }
                                }

                                return `Total: € ${total.toFixed(2)}`
                            }
                        },
                    },
                    scales: {
                        xAxes: [
                            {
                                barPercentage: 1.2,
                                stacked: true,
                                gridLines: false,
                                ticks: {
                                    display: true
                                }
                            },
                        ],
                        yAxes: [
                            {
                                stacked: true,
                                ticks: {
                                    userCallback(label) {
                                        return label > 999 ? `${(label / 1000)}k` : label;
                                    },
                                },
                            },
                        ],
                    },
                },
                ...this.chartOptions,
            };

            this.chart = new Chart(this.$refs.canvas, {
                type: 'bar',
                data: this.monthlySalesData,
                options: chartOptions,
            });

            // Generate the chart labels.
            this.$refs.legend.innerHTML = this.chart.generateLegend();

            // Hide initially the first and last chart points.
            // They can still be triggered on hover.
            if (this.monthlySalesData.datasets.length) {
                const meta = this.chart.getDatasetMeta(0);
                meta.data[0]._model.radius = 0;
                meta.data[this.monthlySalesData.datasets[0].data.length - 1]._model.radius = 0;
            }

            // Render the chart.
            this.chart.render();

            this.chart.options.scales.xAxes[0].ticks.display = this.chart.height < 140 ? false : true;
            this.chart.options.tooltips.enabled = this.chart.height < 140 ? false : true;
            this.chart.update()
        },

        watch: {
            graphData: {
                handler: function () {
                    this.updateChart()
                },

                deep: true
            }
        }
    };
</script>

